import React, { useRef, useState } from 'react';

import { SelectFile } from './components/SelectFile';
import { ReadFile } from './promiseReadFile';

// You normally can't store a function in a `useState` hook since React will interpret new values as a functional update
//   when set.  This hook provides a way to work around that by storing the actual function within a container.
function useFunctionState<T>(initialValue: T) {
  const [container, setContainer] = useState<{ value: T }>({ value: initialValue });

  return [container.value, (value: T) => setContainer({ value })] as const;
}

/**
 * A hook to enable programmatic selection of a file.
 *
 * @param accept The types of file that should be uploadable.
 * @param multiple If true, multiple files can be selected.
 *
 * @returns
 *   1) A function that should be called during your render method in order to inject a hidden selection component.
 *   2) A function that can be called in order to trigger the file selection dialog.  It returns a promise that will
 *        resolve to the selected file.
 *
 * @example
 *
 * const [renderFileSelect, selectFile] = useHiddenFileSelect('.tre,.pcd,', false);
 * function processUploadedFile(file: ReadFile) {
 *   // ...
 * }
 * // ...
 * return (
 *   <>
 *     <button onClick={() => selectFile().then(processUploadedFile)} />
 *     {renderFileSelect()}
 *   </>
 * );
 */
export function useHiddenFileSelect<TMultiple extends true | false>(accept: string, multiple: TMultiple) {
  type TResult = true extends TMultiple ? ReadFile[] : ReadFile;

  const component = useRef<SelectFile>(null);
  const [resolve, setResolve] = useFunctionState<((file: TResult) => void) | null>(null);

  async function onUpload(files: readonly Promise<ReadFile>[]) {
    if (!resolve) {
      return;
    }

    if (multiple) {
      resolve((await Promise.all(files)) as TResult);
    } else {
      resolve((await files[0]) as TResult);
    }
  }

  function render() {
    return <SelectFile hide accept={accept} multiple={multiple} ref={component} onChange={onUpload} />;
  }

  function selectFile() {
    component.current!.click();

    return new Promise<TResult>((newResolve) => {
      setResolve(newResolve);
    });
  }

  return [render, selectFile] as const;
}
